import clsx from "clsx";
import React from "react";
import { useTranslation } from "react-i18next";
import LoadingIcon from "components/LoadingIcon";
import { IconFileUpload, IconPlus } from "@tabler/icons-react";
import ReadyToUploadImages from "./ReadyToUploadImages";
import { PhotographIcon } from "@heroicons/react/outline";
import useToast from "hooks/useToast";

interface ImageUploaderLocalProps extends React.HTMLProps<HTMLInputElement> {
  containerClassName?: string;
  labelName?: string;
  images?: File[];
  onRemove?: (id: string) => void;
  handleChange?: (images: File[]) => void | Promise<void>;
  multiple?: boolean;
  onDraged?: (filesList: File[]) => void;
  onRemoveSelected?: (file: File) => void;
  buttonText?: string;
  uploadedImagesLength?: number;
  onlyImage?: boolean;
  setInProgress?: (value: boolean) => void;
  maxFileSize?: number; // in bytes
}

type SelectedFiles = File[];

const ImageUploaderLocal = React.forwardRef(
  (props: ImageUploaderLocalProps, ref: React.Ref<HTMLInputElement>) => {
    const { t } = useTranslation();
    const [selectedFiles, setSelectedFiles] = React.useState<SelectedFiles>(
      props.images ?? []
    );
    const [compressing, setCompressing] = React.useState(false);
    const [isDragging, setIsDragging] = React.useState(false);
    const toast = useToast();

    React.useEffect(() => {
      props.setInProgress?.(compressing);
    }, [compressing]);

    const LoadingView = () => {
      return (
        <div
          className={clsx(
            "group border rounded-md flex flex-wrap py-4 whitespace-normal items-center",
            "justify-center flex-col w-[100px] h-[100px] font-[500] text-[12px] text-gray-500"
          )}
        >
          <LoadingIcon type="primary" className="w-7 h-7" />
        </div>
      );
    };

    const handleImageCompress = async (files: File[] | null) => {
      if (!files) return;

      setCompressing(true);
      const promises: Promise<any>[] = [];

      for (const element of files) {
        const file = element;
        promises.push(processFile(file));
      }

      try {
        const results = await Promise.all(promises);
        setSelectedFiles(results as File[]);
        handleChange(results as File[]).catch((err) => console.error(err));
        setCompressing(false);
        return results;
      } catch (error) {
        console.error("An error occurred:", error);
      }
    };

    const processFile = (file: File) => {
      return new Promise((resolve) => {
        const maxSizeInBytes = 1 * 1024 * 1024; // Maximum file size in bytes (1MB)
        const maxWidthOrHeight = 2000; // Maximum width or height in pixels
        const quality = 0.8; // Quality parameter (80%)
        if (file.size > maxSizeInBytes) {
          const image = new Image();
          image.src = URL.createObjectURL(file);

          new Promise<File>(() => {
            image.onload = () => {
              let width = image.width;
              let height = image.height;

              if (width > height && width > maxWidthOrHeight) {
                height *= maxWidthOrHeight / width;
                width = maxWidthOrHeight;
              } else if (height > maxWidthOrHeight) {
                width *= maxWidthOrHeight / height;
                height = maxWidthOrHeight;
              }

              const canvas = document.createElement("canvas");
              canvas.width = width;
              canvas.height = height;

              const context = canvas.getContext("2d");
              if (context) {
                context.drawImage(image, 0, 0, width, height);

                canvas.toBlob(
                  (compressedBlob) => {
                    if (compressedBlob) {
                      const compressedFile = new File(
                        [compressedBlob],
                        file.name,
                        {
                          type: file.type,
                          lastModified: Date.now(),
                        }
                      );
                      resolve(compressedFile);
                    } else {
                      console.error("Could not generate compressed blob");
                      resolve(file);
                    }
                  },
                  file.type,
                  quality
                );
              } else {
                console.error("Could not retrieve canvas 2D context");
                resolve(file); // Fallback to original file
              }
            };
          });
        } else {
          resolve(file);
        }
      });
    };

    async function removeFile(file: File) {
      const files = selectedFiles.filter((item) => {
        if (file instanceof File && item instanceof File) {
          return item !== file;
        }
        return item;
      });
      if (props.handleChange && props.images) {
        await props.handleChange?.(files);
      } else {
        setSelectedFiles(files);
        await props.handleChange?.(files);
        props.onRemoveSelected?.(file);
      }
    }

    function checkAllowedFileSize(imageFiles: File[]) {
      const validFiles = imageFiles.filter(
        (file) => !props.maxFileSize || file.size <= props.maxFileSize
      );

      if (props.maxFileSize && validFiles.length != imageFiles.length) {
        toast(
          t("commons.maxAllowedFileSize", {
            size: props.maxFileSize / 1024 / 1024,
          }),
          "attention"
        );
      }
      return validFiles;
    }

    const handleDrop = async (e: React.DragEvent<HTMLLabelElement>) => {
      e.preventDefault();
      if (e.dataTransfer.files.length) {
        const filesList = Array.from(e.dataTransfer.files);
        //Filter image types
        const imageFiles = filesList.filter(
          (file) => file.type.startsWith("image/") && file.type !== "image/tiff"
        );

        const validFiles = checkAllowedFileSize(imageFiles);

        if (validFiles.length === 0) {
          setIsDragging(false);
          return;
        }

        setIsDragging(false);
        await handleImageCompress(validFiles)
          .then((response) => {
            props.onDraged?.(response as File[]);
          })
          .catch((error) => {
            console.error("Error compressing image: ", error);
            handleChange(e.dataTransfer.files);
          });
      }
    };

    const handleFileAdded = async (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files) {
        const filesList = Array.from(e.target.files);
        const validFiles = checkAllowedFileSize(filesList);

        await handleImageCompress(validFiles)
          .then(() => props.onChange?.(e))
          .catch((error) => {
            console.error("Error compressing image: ", error);
            props.onChange?.(e);
            if (e.target.files) {
              handleChange(e.target.files);
            }
          });
      }
    };

    const onDraggEnter = (e: React.DragEvent<HTMLLabelElement>) => {
      e.preventDefault();
      setIsDragging(true);
    };

    const onDraggLeave = (e: React.DragEvent<HTMLLabelElement>) => {
      e.preventDefault();
      setIsDragging(false);
    };

    const handleChange = async (filesList: File[] | FileList) => {
      const nextState = Array.from(
        new Set([
          ...(props.multiple ? selectedFiles : []),
          ...Array.from(filesList),
        ])
      );
      if (props.handleChange && props.images) {
        await props.handleChange?.(nextState);
      } else {
        setSelectedFiles(nextState);
        await props.handleChange?.(nextState);
      }
    };

    React.useEffect(() => {
      if (props.images) {
        setSelectedFiles(props.images);
      }
    }, [props.images]);

    return (
      <div
        className={clsx("flex flex-col mt-1 mb-2", props.containerClassName)}
      >
        <div className="flex flex-wrap gap-3 items-start">
          {compressing && <LoadingView />}
          {selectedFiles.length && !props.multiple ? null : props.children}
          {selectedFiles.length
            ? selectedFiles?.map((image, i) => (
                <ReadyToUploadImages
                  key={i}
                  onRemove={removeFile}
                  item={image}
                />
              ))
            : null}
          <label
            className={clsx(
              "flex flex-col items-center justify-center p-6 transition border cursor-pointer",
              "border-dashed appearance-none text-gray-700 hover:bg-gray-50",
              "focus:outline-none rounded-md",
              selectedFiles.length || props.uploadedImagesLength || compressing
                ? "w-[100px] h-[100px]"
                : "h-56 w-full",
              isDragging
                ? "bg-indigo-50 border-indigo-600"
                : "bg-white border-gray-300 hover:border-gray-400"
            )}
            onDrop={(e) => handleDrop(e).catch((err) => console.error(err))}
            onDragOver={onDraggEnter}
            onDragEnter={onDraggEnter}
            onDragLeave={onDraggLeave}
          >
            <input
              ref={ref}
              type="file"
              onBlur={props.onBlur}
              name={props.name}
              id={props.id}
              required={props.required}
              className="hidden"
              accept=".jpeg,.jpg,.png,.gif,.svg,.webp"
              multiple={props.multiple}
              onChange={handleFileAdded}
            />
            {selectedFiles.length ||
            props.uploadedImagesLength ||
            compressing ? (
              <div
                className={clsx(
                  "flex px-4 py-3 text-base justify-center items-center font-medium rounded-md"
                )}
              >
                <IconPlus className="h-6 w-6" stroke={2.5} />
              </div>
            ) : (
              <>
                <PhotographIcon
                  width={64}
                  className={clsx(
                    "mb-4",
                    isDragging
                      ? "text-indigo-500 pointer-events-none"
                      : "text-gray-500"
                  )}
                  strokeWidth={1}
                />
                {isDragging ? (
                  <span className="text-sm font-medium text-indigo-700 pointer-events-none">
                    {t("commons.dropHere")}
                  </span>
                ) : (
                  <>
                    <span className="mb-2 text-sm font-medium">
                      {t("commons.dragAndDropMessage")}
                    </span>
                    <span className="mb-3 text-sm">{t("commons.or")}</span>
                    <div
                      className={clsx(
                        "bg-indigo-100 text-indigo-700 hover:bg-indigo-200 h-10",
                        "flex px-4 py-3 text-base justify-center items-center font-medium rounded-md"
                      )}
                    >
                      {props.buttonText || t("commons.selectFiles")}
                      <IconFileUpload
                        className="ml-2 min-h-5 min-w-5"
                        stroke={2}
                      />
                    </div>
                  </>
                )}
              </>
            )}
          </label>
        </div>
      </div>
    );
  }
);

ImageUploaderLocal.displayName = "ImageUploaderLocal";

export default ImageUploaderLocal;
